import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Input from 'components/shared/Input'
import { useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { setShippingInfo, ShippingField } from 'store/order-slice'
import { useAppDispatch } from 'store/hooks'
import { useEffect } from 'react'
import { useApp } from 'store/steps'

const DeliveryByPostForm = () => {
    const { formatMessage } = useIntl()
    const { isTablet } = useResolutions()
    const { getError, handleRemoveError } = useErrorMessages()
    const dispatch = useAppDispatch()
    const { isUnlimitedMode } = useApp()

    const handleInputChange = (field: ShippingField, value: string) => {
        dispatch(setShippingInfo({ field, value }))
    }
    const unmaskValue = (value: string) =>
        value.replaceAll('-', '').replaceAll('_', '')
    const formCaption = isUnlimitedMode
        ? formatMessage({
              id: 'annual.payment.delivery_by_courier_form_caption',
              defaultMessage: 'Adres, na który zostanie wysłana karta',
          })
        : formatMessage({
              id: 'selfcare.payment.voucher_delivery_address',
              defaultMessage: 'Adres, na który zostanie wysłany Voucher',
          })

    useEffect(() => {
        dispatch(setShippingInfo({ field: 'apartment', value: '' }))
        dispatch(setShippingInfo({ field: 'city', value: '' }))
        dispatch(setShippingInfo({ field: 'name', value: '' }))
        dispatch(setShippingInfo({ field: 'postCode', value: '' }))
        dispatch(setShippingInfo({ field: 'street', value: '' }))
        dispatch(setShippingInfo({ field: 'surname', value: '' }))
    }, [])

    return (
        <Box isFullWidth>
            <Text variant="heading4" mb={[16, 24]}>
                {formCaption}
            </Text>
            <Box gap={32} isFullWidth>
                <Box
                    gap={32}
                    direction={['column', 'row']}
                    isFullWidth={isTablet}
                >
                    <Input
                        id="shippingData.name"
                        type="text"
                        placeholder={formatMessage({
                            id: 'global.input.first_name_placeholder',
                            defaultMessage: 'Imię',
                        })}
                        label={formatMessage({
                            id: 'global.input.first_name_label',
                            defaultMessage: 'Imię',
                        })}
                        onValueChange={(value) => {
                            handleRemoveError('shippingData.name')
                            handleInputChange('name', value)
                        }}
                        isError={!!getError('shippingData.name')}
                        errorMessage={getError('shippingData.name')}
                        onBlurChange={(value) =>
                            handleInputChange('name', value)
                        }
                        maxWidth={275}
                        isRequired
                        autofill="given-name"
                        testId="payment-field-shippingData-name"
                    />
                    <Input
                        id="shippingData.surname"
                        type="text"
                        placeholder={formatMessage({
                            id: 'global.input.last_name_placeholder',
                            defaultMessage: 'Nazwisko',
                        })}
                        label={formatMessage({
                            id: 'global.input.last_name_label',
                            defaultMessage: 'Nazwisko',
                        })}
                        onValueChange={(value) => {
                            handleRemoveError('shippingData.surname')
                            handleInputChange('surname', value)
                        }}
                        isError={!!getError('shippingData.surname')}
                        errorMessage={getError('shippingData.surname')}
                        onBlurChange={(value) =>
                            handleInputChange('surname', value)
                        }
                        maxWidth={275}
                        isRequired
                        autofill="family-name"
                        testId="payment-field-shippingData-surname"
                    />
                </Box>
                <Box
                    gap={32}
                    direction={['column', 'row']}
                    align="flex-end"
                    isFullWidth={isTablet}
                >
                    <Input
                        id="shippingData.street"
                        type="text"
                        placeholder={formatMessage({
                            id: 'global.input.street_and_number_placeholder',
                            defaultMessage: 'Ulica i numer budynku',
                        })}
                        label={formatMessage({
                            id: 'global.input.street_and_number_label',
                            defaultMessage: 'Ulica i numer budynku',
                        })}
                        isRequired
                        onValueChange={(value) => {
                            handleRemoveError('shippingData.street')
                            handleInputChange('street', value)
                        }}
                        isError={!!getError('shippingData.street')}
                        errorMessage={getError('shippingData.street')}
                        onBlurChange={(value) =>
                            handleInputChange('street', value)
                        }
                        maxWidth={370}
                        testId="payment-field-shippingData-street"
                    />

                    <Input
                        id="voucherDetail.delivery.apartment"
                        type="text"
                        placeholder={formatMessage({
                            id: 'global.input.apartment_placeholder',
                            defaultMessage: 'Numer lokalu',
                        })}
                        label={`${formatMessage({
                            id: 'global.input.apartment_label',
                            defaultMessage: 'Numer lokalu',
                        })}`}
                        onBlurChange={(value) =>
                            handleInputChange('apartment', value)
                        }
                        onValueChange={(value) =>
                            handleInputChange('apartment', value)
                        }
                        maxWidth={180}
                        isOptional
                        testId="payment-field-shippingData-apartment"
                    />
                </Box>
                <Box
                    gap={32}
                    direction={['column', 'row']}
                    isFullWidth={isTablet}
                >
                    <Input
                        id="shippingData.postCode"
                        type="text"
                        mask={'##-###'}
                        maskPlaceholder={'_'}
                        label={formatMessage({
                            id: 'global.input.zip_code_label',
                            defaultMessage: 'Kod pocztowy',
                        })}
                        isRequired
                        onValueChange={(value) => {
                            handleInputChange('postCode', unmaskValue(value))
                            handleRemoveError('shippingData.postCode')
                        }}
                        isError={!!getError('shippingData.postCode')}
                        errorMessage={getError('shippingData.postCode')}
                        maxWidth={180}
                        testId="payment-field-shippingData-postCode"
                    />

                    <Input
                        id="shippingData.city"
                        type="text"
                        placeholder={formatMessage({
                            id: 'global.input.city_placeholder',
                            defaultMessage: 'Miasto',
                        })}
                        label={formatMessage({
                            id: 'global.input.city_label',
                            defaultMessage: 'Miasto',
                        })}
                        isRequired
                        onValueChange={(value) => {
                            handleInputChange('city', value)
                            handleRemoveError('shippingData.city')
                        }}
                        isError={!!getError('shippingData.city')}
                        errorMessage={getError('shippingData.city')}
                        onBlurChange={(value) =>
                            handleInputChange('city', value)
                        }
                        maxWidth={370}
                        testId="payment-field-shippingData-city"
                    />
                </Box>
                <Input
                    id="shippingData.phone"
                    type="tel"
                    mask={'###-###-###'}
                    maskPlaceholder={'_'}
                    placeholder={formatMessage({
                        id: 'global.input.phone_placeholder',
                        defaultMessage: 'Numer telefonu',
                    })}
                    label={formatMessage({
                        id: 'global.input.phone_label',
                        defaultMessage: 'Numer telefonu',
                    })}
                    infoMessage={formatMessage({
                        id: 'global.input.phone_info_courier',
                        defaultMessage: 'Numer przekażemy kurierowi',
                    })}
                    isRequired
                    onValueChange={(value) => {
                        handleInputChange('phone', unmaskValue(value))
                        handleRemoveError('shippingData.phone')
                    }}
                    isError={!!getError('shippingData.phone')}
                    errorMessage={getError('shippingData.phone')}
                    maxWidth={370}
                    testId="payment-field-shippingData-phone"
                />
            </Box>
        </Box>
    )
}

export default DeliveryByPostForm
